import {
  MatchListItemFragment,
  PlayerDetailItemFragment,
  PlayerTransferFragment,
  SeasonFragment,
} from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import Container from '@amf/shared/components/layout/Container';
import PlayerHeader from '@amf/shared/components/player/PlayerHeader';
import TeamMatches from '@amf/shared/components/team/TeamMatches';
import { getMatches, LIMIT } from '@amf/shared/data/Match';
import { useTranslate } from '@tolgee/react';
import { getPlayerTransfers } from '@amf/shared/data/Player';
import PlayerTransfers from '@amf/shared/components/player/PlayerTransfers';
import PlayerNews from '@amf/shared/components/player/PlayerNews';
import Content from '@amf/shared/components/content/Content';
import PlayerStats from '@amf/shared/components/player/PlayerStats';
import PlayerLastMatches from '@amf/shared/components/player/PlayerLastMatches';
import PlayerGoalKeeperStats from '@amf/shared/components/player/PlayerGoalKeeperStats';
import PlayerUnions from '@amf/shared/components/player/PlayerUnions';

import { AppLinks } from 'utils/links';
import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';

interface PlayerDetailPageProps extends NavigationProps {
  player: PlayerDetailItemFragment;
  seasons: SeasonFragment[];
  matches: MatchListItemFragment[];
  transfers: PlayerTransferFragment[];
}

export default function PlayerDetailPage({
  navigation,
  partners,
  player,
  seasons,
  matches,
  transfers,
}: PlayerDetailPageProps) {
  const { t } = useTranslate();
  return (
    <div className='PlayerDetailPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={player.fullName} image={player.photoUrl} />
        <PlayerHeader player={player} prefixes={{ team: AppLinks.team }} />
        <Container>
          <PlayerLastMatches
            title={t('player.lastMatches.title')}
            player={player}
            prefixes={{ match: AppLinks.match }}
            disableAssists
            disablePoints
          />
          <PlayerStats
            title={t('player.stats.title')}
            player={player}
            disableAssists
            disablePoints
          />
          <PlayerGoalKeeperStats title={t('player.stats.goalKeeper.title')} player={player} />
          {player.content && <Content content={player.content} />}
          {player.news.length > 0 && (
            <PlayerNews
              title={t('player.news.title')}
              news={player.news}
              prefixes={{ news: AppLinks.newsList }}
            />
          )}
          <TeamMatches
            title={t('player.matches.title')}
            seasons={seasons}
            player={player}
            matches={matches}
            matchPrefix={AppLinks.match}
            disableAssists
            disablePoints
          />
          <PlayerTransfers
            title={t('player.transfers.title')}
            transfers={transfers}
            teamPrefix={AppLinks.team}
            player={player}
          />
          <PlayerUnions player={player} />
        </Container>
      </Layout>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<PlayerDetailPageProps, { id: string }> =
  async ({ locale, params, res }) => {
    const { id } = params;
    const { player } = await sdk.PlayerDetail({
      id,
      union: process.env.NEXT_PUBLIC_UNION,
    });

    if (!player) {
      return {
        notFound: true,
      };
    }

    res.setHeader('Cache-Control', 'max-age=0, s-maxage=3600');
    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });

    const filteredSeasons = seasons.filter(s => s.leagues.length > 0);
    let matches: MatchListItemFragment[] = [];
    if (filteredSeasons.length > 0) {
      matches = await getMatches(filteredSeasons[0].leagues[0].id, 0, LIMIT, null, player.id);
    }

    const transfers = await getPlayerTransfers(player.id);

    return {
      props: {
        player,
        seasons,
        matches,
        transfers,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
